import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingInterval } from '@this+that/models';
import log from 'loglevel';

import { useBillingCurrency } from './useBillingCurrency';
import {
  useCancelTeamSubscription,
  useCreateBillingCheckoutSession,
  useCreateBillingPortalSession,
  useGetTeamBilling,
} from '../../services/billing.service';
import { isTeamCacheStale, useTeamStore } from '../../store/Team/useTeamStore';
import { isAbortError } from '../../utils/isAbortError';
import { useCustomToast } from '../useCustomToast';

export function useTeamBilling(teamId: string) {
  const { t } = useTranslation();
  const { errorMessage, successMessage } = useCustomToast();
  const currency = useBillingCurrency();

  const [loading, setLoading] = useState(
    () => !useTeamStore.getState().billingByTeamId[teamId],
  );
  const loadRequestRef = useRef(0);

  const cachedBilling = useTeamStore((s) => s.billingByTeamId[teamId]);
  const billing = cachedBilling?.billing;

  const { getTeamBilling } = useGetTeamBilling();
  const { createBillingCheckoutSession, loading: checkoutLoading } =
    useCreateBillingCheckoutSession();
  const { createBillingPortalSession, loading: portalLoading } =
    useCreateBillingPortalSession();
  const { cancelTeamSubscription, loading: cancelLoading } =
    useCancelTeamSubscription();

  const loadBilling = useCallback(
    async (force = false) => {
      const store = useTeamStore.getState();
      if (
        !force &&
        !isTeamCacheStale(store.billingByTeamId[teamId]?.loadedAt)
      ) {
        setLoading(false);
        return;
      }

      const requestId = loadRequestRef.current + 1;
      loadRequestRef.current = requestId;
      setLoading(true);

      try {
        const result = await getTeamBilling(teamId);
        if (requestId === loadRequestRef.current && result) {
          useTeamStore.getState().setTeamBilling(teamId, result);
        }
      } catch (err) {
        if (isAbortError(err)) {
          return;
        }
        if (requestId === loadRequestRef.current) {
          log.error('useTeamBilling.loadBilling', err);
          errorMessage(t('teamHub.billing.toasts.loadError'));
        }
      } finally {
        if (requestId === loadRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [teamId, getTeamBilling, errorMessage, t],
  );

  useEffect(() => {
    loadBilling();
  }, [teamId]);

  const checkout = async (interval: BillingInterval) => {
    try {
      const url = await createBillingCheckoutSession(
        teamId,
        interval,
        currency,
      );
      if (!url) {
        errorMessage(t('teamHub.billing.toasts.checkoutError'));
        return;
      }
      window.location.assign(url);
    } catch (err) {
      log.error('useTeamBilling.checkout', err);
      errorMessage(t('teamHub.billing.toasts.checkoutError'));
    }
  };

  const openPortal = async () => {
    try {
      const url = await createBillingPortalSession(teamId);
      if (!url) {
        errorMessage(t('teamHub.billing.toasts.portalError'));
        return;
      }
      window.location.assign(url);
    } catch (err) {
      log.error('useTeamBilling.openPortal', err);
      errorMessage(t('teamHub.billing.toasts.portalError'));
    }
  };

  const cancelSubscription = async (): Promise<boolean> => {
    try {
      const ok = await cancelTeamSubscription(teamId);
      if (!ok) {
        errorMessage(t('teamHub.billing.toasts.cancelError'));
        return false;
      }
      useTeamStore.getState().patchTeam(teamId, { cancelAtPeriodEnd: true });
      successMessage(t('teamHub.billing.toasts.cancelSuccess'));
      return true;
    } catch (err) {
      log.error('useTeamBilling.cancelSubscription', err);
      errorMessage(t('teamHub.billing.toasts.cancelError'));
      return false;
    }
  };

  return {
    billing,
    loading,
    checkout,
    checkoutLoading,
    openPortal,
    portalLoading,
    cancelSubscription,
    cancelLoading,
    reload: () => loadBilling(true),
  };
}
